'use client'

import { Image, Text } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useRef, useState } from 'react'

import { useImpressionLog } from '@/features/seoPlaceDetail/model'
import { type ISeoNearbyPlace } from '@/features/seoPlaceDetail/model'
import { MycleLogoDefault } from '@/shared/assets/icons'
import { logEvent } from '@/shared/external/firebase/analytics'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'

import PlaceSectionDivider from '../PlaceSectionDivider/PlaceSectionDivider'

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.NEARBY_SHOP,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.NEARBY_SHOP,
}

interface IPlaceNearbyProps {
  nearbyPlaces: ISeoNearbyPlace[]
}

const PlaceNearby = ({ nearbyPlaces }: IPlaceNearbyProps) => {
  const sectionRef = useRef<HTMLElement | null>(null)
  const isVisible = nearbyPlaces.length > 0

  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
    enabled: isVisible,
  })

  if (!isVisible) return null

  return (
    <>
      <PlaceSectionDivider />
      <section
        ref={sectionRef}
        aria-label='주변 정비소'
        className='flex flex-col gap-dimension-x4 bg-background-surface-default py-dimension-x6'
      >
        <div className='px-dimension-x5'>
          <Text
            as='h2'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
          >
            주변 정비소
          </Text>
        </div>

        <div className='overflow-x-auto'>
          <div className='flex gap-dimension-x3 pl-dimension-x5'>
            {nearbyPlaces.map((place, index) => (
              <NearbyPlaceCard
                key={place.serverId}
                place={place}
                index={index}
              />
            ))}
            {/* 스크롤 끝 24px 여백 (gap 12px + spacer 12px = 24px) */}
            <div aria-hidden className='w-[12px] shrink-0' />
          </div>
        </div>
      </section>
    </>
  )
}

interface INearbyPlaceCardProps {
  place: ISeoNearbyPlace
  index: number
}

const NearbyPlaceCard = ({ place, index }: INearbyPlaceCardProps) => {
  const [hasImageError, setHasImageError] = useState(false)

  const imageUrl = place.firstImage?.url ?? null
  const address = place.streetAddress || ''
  const reviewCount = place.reviewCount ?? 0
  const distanceText = formatDistance(place.distance)
  const canShowImage = imageUrl != null && !hasImageError

  const handleClick = () => {
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, {
      code: CODE.COMMON_WEB_PLACE.NEARBY_SHOP,
      type: TYPE.CARD,
      label: String(index),
    })
  }

  const cardContent = (
    <>
      <div className='relative h-[112px] w-full shrink-0 overflow-hidden'>
        {canShowImage ? (
          <Image
            src={imageUrl}
            alt={place.placeName}
            width='100%'
            height='100%'
            radius={0}
            className='absolute inset-0'
            onError={() => setHasImageError(true)}
          />
        ) : (
          <div className='flex size-full items-center justify-center bg-foreground-neutral-muted'>
            <MycleLogoDefault fill='#FFFFFF' width='120' height='24' />
          </div>
        )}
      </div>
      <div className='flex flex-col px-dimension-x4 pt-dimension-x3'>
        <Text
          variant='title-2-semibold'
          color='text-foreground-neutral-strong'
          className='truncate'
        >
          {place.placeName}
        </Text>
        <Text
          variant='body-2-regular'
          color='text-foreground-neutral-assistive'
          className='truncate mt-[1.5px]'
        >
          {address}
        </Text>
        <div className='flex items-center gap-dimension-x1-5 mt-[5px]'>
          {distanceText && (
            <>
              <Text
                variant='body-1-medium'
                color='text-foreground-brand-strong'
                className='whitespace-nowrap'
              >
                {distanceText}
              </Text>
              <span className='h-[12px] w-px shrink-0 bg-foreground-neutral-muted' />
            </>
          )}
          <Text
            variant='body-1-medium'
            color='text-foreground-neutral-alternative'
            className='whitespace-nowrap'
          >
            정비 후기 {reviewCount.toLocaleString()}
          </Text>
        </div>
      </div>
    </>
  )

  const cardClass =
    'flex h-[206px] w-[212px] shrink-0 flex-col overflow-hidden rounded-dimension-x3 border border-stroke-neutral-strong'

  if (place.hashId) {
    return (
      <Link
        href={`/place/${encodeURIComponent(place.hashId)}`}
        onClick={handleClick}
        className={cardClass}
        aria-label={`${place.placeName} 상세 페이지로 이동`}
      >
        {cardContent}
      </Link>
    )
  }

  return <article className={cardClass}>{cardContent}</article>
}

// distance는 km 단위 (서버가 이미 변환해서 내려줌, 예: 4.152809...)
const formatDistance = (distance: number | null | undefined): string => {
  if (distance == null || !Number.isFinite(distance)) return ''
  if (distance < 10) return `${distance.toFixed(1)}km`
  return `${Math.round(distance)}km`
}

export default PlaceNearby
